import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import axios from "axios";

const BASE_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
).replace(/\/$/, "");

// ==========================
// GET CLUBS
// ==========================
export const getClubs =
  createAsyncThunk(
    "clubs/getClubs",
    async (
      _,
      { rejectWithValue }
    ) => {
      try {
        let user: any = {};

        try {
          user = JSON.parse(
            localStorage.getItem(
              "user"
            ) || "{}"
          );
        } catch {
          user = {};
        }

        const params: any = {};

        if (user?.user_id) {
          params.user_id =
            user.user_id;
        }

        const res =
          await axios.get(
            `${BASE_URL}/client/clubs`,
            {
              params,
            }
          );

        return (
          res.data?.data ||
          res.data?.clients ||
          []
        );
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data
            ?.message ||
            "Failed to fetch clubs"
        );
      }
    }
  );

// ==========================
// GET CLUB CATEGORIES
// ==========================
export const getClubCategories =
  createAsyncThunk(
    "clubs/getClubCategories",
    async (
      _,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await axios.get(
            `${BASE_URL}/client/club-categories`
          );

        return (
          res.data?.data ||
          []
        );
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data
            ?.message ||
            "Failed to fetch club categories"
        );
      }
    }
  );

// ==========================
// GET CLUB PROFILE
// ==========================
export const getClubProfile =
  createAsyncThunk(
    "clubs/getClubProfile",
    async (
      client_id: number,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await axios.get(
            `${BASE_URL}/client/profile/${client_id}`
          );

        return (
          res.data?.data ||
          null
        );
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data
            ?.message ||
            "Failed to fetch club profile"
        );
      }
    }
  );

// ==========================
// GET CLUB PROFILE BY SLUG
// ==========================
export const getClubProfileBySlug =
  createAsyncThunk(
    "clubs/getClubProfileBySlug",
    async (
      slug: string,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await axios.get(
            `${BASE_URL}/client/public/${encodeURIComponent(
              slug
            )}`
          );

        return (
          res.data?.data ||
          null
        );
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data
            ?.message ||
            "Failed to fetch club profile"
        );
      }
    }
  );

// ==========================
// GET CLUB PUBLIC POSTS
// ==========================
export const getClubPublicPosts =
  createAsyncThunk(
    "clubs/getClubPublicPosts",
    async (
      client_id: number,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await axios.get(
            `${BASE_URL}/client/public-posts/${client_id}`
          );

        return (
          res.data?.data ||
          res.data?.posts ||
          []
        );
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data
            ?.message ||
            "Failed to fetch club posts"
        );
      }
    }
  );

interface ClubState {
  loading: boolean;
  postsLoading: boolean;
  profileLoading: boolean;

  clubs: any[];
  categories: any[];
  clubPosts: any[];

  clubProfile: any | null;

  error: string | null;
}

const initialState: ClubState = {
  loading: false,
  postsLoading: false,
  profileLoading: false,

  clubs: [],
  categories: [],
  clubPosts: [],

  clubProfile: null,

  error: null,
};

const clubSlice =
  createSlice({
    name: "clubs",

    initialState,

    reducers: {},

    extraReducers: (
      builder
    ) => {
      builder
        // ==========================
        // GET CLUBS
        // ==========================
        .addCase(
          getClubs.pending,
          (state) => {
            state.loading =
              true;

            state.error =
              null;
          }
        )

        .addCase(
          getClubs.fulfilled,
          (
            state,
            action
          ) => {
            state.loading =
              false;

            state.clubs =
              action.payload;
          }
        )

        .addCase(
          getClubs.rejected,
          (
            state,
            action
          ) => {
            state.loading =
              false;

            state.error =
              action.payload as string;
          }
        )

        // ==========================
        // GET CLUB CATEGORIES
        // ==========================
        .addCase(
          getClubCategories.pending,
          (state) => {
            state.loading =
              true;

            state.error =
              null;
          }
        )

        .addCase(
          getClubCategories.fulfilled,
          (
            state,
            action
          ) => {
            state.loading =
              false;

            state.categories =
              action.payload;
          }
        )

        .addCase(
          getClubCategories.rejected,
          (
            state,
            action
          ) => {
            state.loading =
              false;

            state.error =
              action.payload as string;
          }
        )

        // ==========================
        // GET CLUB POSTS
        // ==========================
        .addCase(
          getClubPublicPosts.pending,
          (state) => {
            state.postsLoading =
              true;

            state.error =
              null;
          }
        )

        .addCase(
          getClubPublicPosts.fulfilled,
          (
            state,
            action
          ) => {
            state.postsLoading =
              false;

            state.clubPosts =
              action.payload;
          }
        )

        .addCase(
          getClubPublicPosts.rejected,
          (
            state,
            action
          ) => {
            state.postsLoading =
              false;

            state.error =
              action.payload as string;
          }
        )

        // ==========================
        // GET CLUB PROFILE
        // ==========================
        .addCase(
          getClubProfile.pending,
          (state) => {
            state.profileLoading =
              true;

            state.error =
              null;
          }
        )

        .addCase(
          getClubProfile.fulfilled,
          (
            state,
            action
          ) => {
            state.profileLoading =
              false;

            state.clubProfile =
              action.payload;
          }
        )

        .addCase(
          getClubProfile.rejected,
          (
            state,
            action
          ) => {
            state.profileLoading =
              false;

            state.error =
              action.payload as string;
          }
        )

        // ==========================
        // GET CLUB PROFILE BY SLUG
        // ==========================
        .addCase(
          getClubProfileBySlug.pending,
          (state) => {
            state.profileLoading =
              true;

            state.error =
              null;

            state.clubProfile =
              null;
          }
        )

        .addCase(
          getClubProfileBySlug.fulfilled,
          (
            state,
            action
          ) => {
            state.profileLoading =
              false;

            state.clubProfile =
              action.payload;
          }
        )

        .addCase(
          getClubProfileBySlug.rejected,
          (
            state,
            action
          ) => {
            state.profileLoading =
              false;

            state.error =
              action.payload as string;
          }
        );
    },
  });

export default clubSlice.reducer;
